import { useState, useEffect } from 'react';
import { X, Save, Key } from 'lucide-react';
import { userStore } from '../../stores/user.store';
import { Button } from "../design/Button";
import { Input } from "../design/Input";
import { observer } from 'mobx-react-lite';
import toast from 'react-hot-toast';
import type { User } from '../../types';

interface ResetPasswordModalProps {
    isOpen: boolean;
    onClose: () => void;
    user: User | null;
}

const ResetPasswordModal = observer(({ isOpen, onClose, user }: ResetPasswordModalProps) => {
    const [newPassword, setNewPassword] = useState('');
    const [loading, setLoading] = useState(false);

    // Reset state when modal opens
    useEffect(() => {
        if (isOpen) {
            setNewPassword('');
            setLoading(false);
        }
    }, [isOpen]);

    if (!isOpen || !user) return null;

    const handleSave = async (e: React.FormEvent) => {
        e.preventDefault();
        if (newPassword.length < 5) {
            toast.error("Passwort muss mindestens 5 Zeichen lang sein.");
            return;
        }

        setLoading(true);
        const res = await userStore.resetPassword(user.id, newPassword);
        setLoading(false);

        if (res.success) {
            toast.success(`Passwort für ${user.username} zurückgesetzt!`);
            onClose();
        } else {
            toast.error(res.message || "Fehler beim Zurücksetzen.");
        }
    };

    return (
        <div className="fixed inset-0 bg-black/80 flex items-center justify-center z-50 p-4">
            <div className="bg-surface border border-line rounded-2xl w-full max-w-md overflow-hidden shadow-2xl">
                <div className="p-6 border-b border-line flex justify-between items-center bg-ink/50">
                    <h3 className="text-xl font-bold text-white flex items-center">
                        <Key className="mr-2 text-yellow-500" size={20} />
                        Passwort zurücksetzen
                    </h3>
                    <button onClick={onClose} className="p-2 text-fg-muted hover:text-white hover:bg-surface-2 rounded-lg transition-all">
                        <X size={24} />
                    </button>
                </div>
                
                <form onSubmit={handleSave} className="p-8 space-y-6">
                    <div className="bg-yellow-900/20 border border-yellow-500/20 rounded-lg p-4 mb-4">
                        <p className="text-sm text-yellow-200">
                            Du setzt das Passwort für <strong className="text-white">{user.username}</strong> zurück.
                            Der Nutzer wird beim nächsten Login aufgefordert, das Passwort zu ändern.
                        </p>
                    </div>

                    <div>
                        <label className="block text-xs uppercase text-fg-muted font-bold mb-2">Neues temporäres Passwort</label>
                        <input 
                            type="text" 
                            value={newPassword}
                            onChange={e => setNewPassword(e.target.value)}
                            className="w-full bg-gray-950 border border-line rounded-lg p-3 text-white focus:border-yellow-500 outline-none font-mono"
                            placeholder="z.B. TempPass123"
                            autoFocus
                        />
                    </div>

                    <div className="flex justify-end space-x-3 pt-4">
                        <button 
                            type="button"
                            onClick={onClose} 
                            className="px-6 py-2 rounded-lg text-fg-muted hover:text-white hover:bg-surface-2 transition-all"
                        >
                            Abbrechen
                        </button>
                        <button 
                            type="submit" 
                            disabled={loading || newPassword.length < 1}
                            className="px-6 py-2 rounded-lg bg-yellow-600 hover:bg-yellow-500 text-white font-bold shadow-lg shadow-yellow-900/20 transition-all flex items-center disabled:opacity-50 disabled:cursor-not-allowed"
                        >
                            {loading ? 'Speichere...' : <><Save size={18} className="mr-2" /> Reset & Force Change</>}
                        </button>
                    </div>
                </form>
            </div>
        </div>
    );
});

export default ResetPasswordModal;
